import { useEffect, useState } from "react";
import { RefreshCw, TrendingUp, Flame, Mail, Phone, Copy, Send } from "lucide-react";
import api from "@/lib/api";
import { toast } from "sonner";

const APT_LABEL = {
    "erdgeschoss": "Erdgeschoss",
    "obergeschoss": "Obergeschoss",
    "penthouse": "Penthouse",
    "loft-maisonette": "Loft-Maisonette",
};

const SCENARIO_LABEL = {
    "50k":  "50 k €",
    "100k": "100 k €",
    "200k": "200 k €",
    "be":   "Break-even",
};

const fmtDate = (iso) => {
    if (!iso) return "—";
    try {
        return new Date(iso).toLocaleString("de-DE", {
            day: "2-digit", month: "2-digit", year: "numeric",
            hour: "2-digit", minute: "2-digit",
        });
    } catch {
        return iso;
    }
};

/** Admin panel: heatmap of wealth-plan requests (4 apartments × 4 equity scenarios).
 *  Complements the generic /inquiries list with a marketing-analytics lens. */
export const WealthLeadsPanel = () => {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [sendingDigest, setSendingDigest] = useState(false);

    const load = async () => {
        setLoading(true);
        try {
            const res = await api.get("/admin/wealth-plan-stats");
            setData(res.data);
        } catch (err) {
            toast.error("Konnte Vermögens-Leads nicht laden", { description: err.message });
        } finally {
            setLoading(false);
        }
    };

    const sendDigest = async () => {
        setSendingDigest(true);
        try {
            const res = await api.post("/admin/digest/send-now");
            const { status, new: newCount, wealth, project, to } = res.data || {};
            if (status === "sent") {
                toast.success("Tagesreport versendet", {
                    description: `${newCount || 0} neue Leads (${wealth || 0} Vermögen, ${project || 0} Projekt) → ${to}`,
                    duration: 6000,
                });
            } else if (status === "skipped-no-smtp") {
                toast.warning("SMTP nicht konfiguriert", { description: "Der Tagesreport wurde nicht versendet." });
            } else {
                toast.success("Tagesreport ausgelöst", { description: JSON.stringify(res.data) });
            }
        } catch (err) {
            toast.error("Tagesreport konnte nicht versendet werden", { description: err.message });
        } finally {
            setSendingDigest(false);
        }
    };

    useEffect(() => { load(); }, []);

    if (loading && !data) {
        return <div className="py-12 text-center text-stone-500 font-body text-sm">Lädt…</div>;
    }
    if (!data) return null;

    // Max cell value for colour intensity
    const maxCell = Math.max(
        1,
        ...data.apartments.flatMap((ap) => data.scenarios.map((sc) => data.matrix[ap]?.[sc] || 0)),
    );

    // Hottest combination
    let hottest = null;
    for (const ap of data.apartments) {
        for (const sc of data.scenarios) {
            const c = data.matrix[ap]?.[sc] || 0;
            if (c > 0 && (!hottest || c > hottest.count)) hottest = { apt: ap, sc, count: c };
        }
    }

    const copyEmail = (email) => {
        navigator.clipboard.writeText(email).then(
            () => toast.success("E-Mail kopiert", { description: email }),
            () => toast.error("Konnte nicht kopieren"),
        );
    };

    return (
        <div className="space-y-10" data-testid="admin-wealth-leads">
            {/* Summary strip */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-px bg-stone-200 border border-stone-200">
                <Metric label="Leads gesamt" value={data.total} testid="wealth-total" />
                <Metric
                    label="Top-Wohnung"
                    value={
                        Object.entries(data.per_apartment)
                            .sort((a, b) => b[1] - a[1])
                            .filter(([, v]) => v > 0)
                            .map(([k]) => APT_LABEL[k] || k)[0] || "—"
                    }
                    testid="wealth-top-apt"
                />
                <Metric
                    label="Top-Eigenkapital"
                    value={
                        Object.entries(data.per_scenario)
                            .sort((a, b) => b[1] - a[1])
                            .filter(([, v]) => v > 0)
                            .map(([k]) => SCENARIO_LABEL[k])[0] || "—"
                    }
                    testid="wealth-top-sc"
                />
                <Metric
                    label="Hottest Kombi"
                    value={
                        hottest
                            ? `${APT_LABEL[hottest.apt]} · ${SCENARIO_LABEL[hottest.sc]}`
                            : "—"
                    }
                    testid="wealth-hottest"
                />
            </div>

            {/* Heatmap */}
            <div>
                <div className="flex items-center justify-between mb-3 flex-wrap gap-3">
                    <div className="flex items-center gap-2">
                        <Flame size={16} className="text-brand-500" />
                        <h3 className="font-heading text-xl text-ink">Anfrage-Heatmap</h3>
                    </div>
                    <div className="flex items-center gap-2">
                        <button
                            onClick={sendDigest}
                            disabled={sendingDigest}
                            className="inline-flex items-center gap-2 px-3 py-2 border border-ink text-ink text-xs font-body hover:bg-ink hover:text-white transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
                            data-testid="wealth-send-digest"
                            title="Tagesreport jetzt an buero@neubau-kaufen-wiesbaden.de senden"
                        >
                            <Send size={14} className={sendingDigest ? "animate-pulse" : ""} />
                            {sendingDigest ? "Wird gesendet…" : "Tagesreport jetzt senden"}
                        </button>
                        <button onClick={load} className="btn-ghost" data-testid="wealth-refresh">
                            <RefreshCw size={14} className={loading ? "animate-spin" : ""} />
                            Aktualisieren
                        </button>
                    </div>
                </div>
                <p className="font-body text-xs text-stone-500 mb-4">
                    Je dunkler die Zelle, desto häufiger wurde diese Kombination angefragt. Nutze das, um im
                    Beratungsgespräch die passende Finanzierungsrange zu betonen. Der Tagesreport läuft automatisch
                    Mo–Sa um 12:00 Uhr.
                </p>
                <div className="overflow-x-auto border border-stone-200">
                    <table className="w-full text-sm" data-testid="wealth-heatmap">
                        <thead>
                            <tr className="border-b border-stone-200 bg-cream">
                                <th className="text-left py-3 px-4 font-body text-[11px] uppercase tracking-[0.14em] text-stone-500">Wohnung ↓ / EK →</th>
                                {data.scenarios.map((sc) => (
                                    <th key={sc} className="py-3 px-4 text-center font-body text-[11px] uppercase tracking-[0.14em] text-stone-500">
                                        {SCENARIO_LABEL[sc]}
                                    </th>
                                ))}
                                <th className="py-3 px-4 text-right font-body text-[11px] uppercase tracking-[0.14em] text-stone-500">Σ</th>
                            </tr>
                        </thead>
                        <tbody>
                            {data.apartments.map((ap) => {
                                const rowTotal = data.per_apartment[ap] || 0;
                                return (
                                    <tr key={ap} className="border-b border-stone-200">
                                        <td className="py-3 px-4 font-heading text-ink">{APT_LABEL[ap] || ap}</td>
                                        {data.scenarios.map((sc) => {
                                            const c = data.matrix[ap]?.[sc] || 0;
                                            const intensity = c / maxCell; // 0..1
                                            const bg = c === 0
                                                ? "bg-white"
                                                : "";
                                            const style = c > 0
                                                ? { backgroundColor: `rgba(217,119,87,${0.12 + intensity * 0.62})` }
                                                : undefined;
                                            return (
                                                <td
                                                    key={sc}
                                                    className={`text-center py-3 px-4 font-heading text-lg ${bg} ${c > 0 ? "text-ink" : "text-stone-300"}`}
                                                    style={style}
                                                    data-testid={`wealth-cell-${ap}-${sc}`}
                                                >
                                                    {c || "·"}
                                                </td>
                                            );
                                        })}
                                        <td className="text-right py-3 px-4 font-heading text-ink">{rowTotal}</td>
                                    </tr>
                                );
                            })}
                            <tr className="bg-cream font-heading">
                                <td className="py-3 px-4 font-body text-[11px] uppercase tracking-[0.14em] text-stone-500">Σ</td>
                                {data.scenarios.map((sc) => (
                                    <td key={sc} className="text-center py-3 px-4 text-ink">
                                        {data.per_scenario[sc] || 0}
                                    </td>
                                ))}
                                <td className="text-right py-3 px-4 text-brand-600">{data.total}</td>
                            </tr>
                        </tbody>
                    </table>
                </div>
            </div>

            {/* Recent leads */}
            <div>
                <div className="flex items-center gap-2 mb-4">
                    <TrendingUp size={16} className="text-brand-500" />
                    <h3 className="font-heading text-xl text-ink">Letzte 20 Vermögensaufbau-Leads</h3>
                </div>
                {data.recent.length === 0 ? (
                    <div className="text-center py-12 border border-dashed border-stone-200 text-stone-400 font-body text-sm">
                        Noch keine Anfragen erhalten.
                    </div>
                ) : (
                    <div className="border border-stone-200 divide-y divide-stone-200" data-testid="wealth-recent-list">
                        {data.recent.map((r) => (
                            <div key={r.id} className="p-4 flex flex-wrap items-start gap-4 hover:bg-cream/40 transition-colors">
                                <div className="flex-1 min-w-[220px]">
                                    <div className="font-heading text-base text-ink">{r.name}</div>
                                    <div className="font-body text-xs text-stone-500 mt-1 flex flex-wrap items-center gap-3">
                                        <a href={`mailto:${r.email}`} className="inline-flex items-center gap-1 hover:text-brand-600">
                                            <Mail size={11} />
                                            {r.email}
                                        </a>
                                        <button
                                            type="button"
                                            onClick={() => copyEmail(r.email)}
                                            className="text-stone-400 hover:text-brand-500"
                                            aria-label="E-Mail kopieren"
                                        >
                                            <Copy size={11} />
                                        </button>
                                        {r.phone && (
                                            <a href={`tel:${r.phone}`} className="inline-flex items-center gap-1 hover:text-brand-600">
                                                <Phone size={11} />
                                                {r.phone}
                                            </a>
                                        )}
                                    </div>
                                </div>
                                <div className="text-right">
                                    <div className="inline-flex items-center gap-2 text-xs font-body">
                                        <span className="px-2 py-0.5 bg-ink text-white">{APT_LABEL[r.apartment] || r.apartment}</span>
                                        <span className="px-2 py-0.5 bg-clay-300 text-ink">{SCENARIO_LABEL[r.equity]}</span>
                                    </div>
                                    <div className="font-body text-[11px] text-stone-400 mt-2">{fmtDate(r.created_at)}</div>
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
};

const Metric = ({ label, value, testid }) => (
    <div className="bg-white p-5" data-testid={testid}>
        <div className="font-body text-[10px] uppercase tracking-[0.16em] text-stone-500">{label}</div>
        <div className="font-heading text-2xl text-ink mt-1 truncate">{value}</div>
    </div>
);
